import { useBottomPanel } from '@ubnt/link-core-ui/components/BottomPanel/context/BottomPanelContext';
import PanelStatusBar, { PanelStatusBarProps } from '@ubnt/link-core-ui/components/BottomPanel/elements/PanelStatusBar';
import bytesFormatter from '@ubnt/ui-components/utils/bytesFormatter';
import { useMemo } from 'react';

import { convertRange } from '@ubnt/wifi-calculator/utils/math';
import { useSelector } from 'app/store';
import {
  linksMaxLinkSpeedSelector,
  linksLinkSpeedSelector,
  linksLinkSpeedImpactedByUplinkSelector,
} from 'modules/links/slice';

const labelTextProps: PanelStatusBarProps['labelTextProps'] = {
  color: 'tertiary',
};

const valueTextProps: PanelStatusBarProps['valueTextProps'] = {
  size: 'body',
  weight: 'normal',
};

const SpeedStatusBar = (props: Partial<PanelStatusBarProps>) => {
  const { linkId } = useBottomPanel();

  const speed = useSelector((state) => (linkId ? linksLinkSpeedSelector(state, linkId) : undefined));
  const speedImpactedByUplink = useSelector((state) =>
    linkId ? linksLinkSpeedImpactedByUplinkSelector(state, linkId) : undefined
  );
  const maxSpeed = useSelector((state) => (linkId ? linksMaxLinkSpeedSelector(state, linkId) : undefined));

  const value = useMemo(() => {
    if (speed == null || maxSpeed == null) {
      return 0;
    }
    if (speed > maxSpeed) {
      return 100;
    }
    return convertRange(speed, [0, maxSpeed], [0, 100]);
  }, [speed, maxSpeed]);

  const valueLabel = useMemo(() => {
    if (speed != null) {
      if (speed === 0) {
        return 'No Link';
      }
      return bytesFormatter((speed * 1000000) / 8, 'bitrate');
    }
    return '-';
  }, [speed]);

  const valueError = speed === 0;
  const showWarning = speedImpactedByUplink && maxSpeed != null && speed != null && maxSpeed > speed;
  const labelWarning =
    showWarning ?
      'Expected Real-World Speed is limited by mesh uplink'
    : 'Based on the signal, channel usage, channel width, bands, client type, and the half-duplex nature of Wi-Fi, this is the approximate speed a client should see.';

  return (
    <PanelStatusBar
      label="Expected Real-World Speed"
      labelTextProps={labelTextProps}
      labelWarning={labelWarning}
      labelWarningSeverity={showWarning ? 'warning' : 'info2'}
      size="large2"
      value={value}
      valueError={valueError}
      valueLabel={valueLabel}
      valueTextProps={valueTextProps}
      {...props}
    />
  );
};

export default SpeedStatusBar;
